import { apiFetch } from '../http';
export const TRAVEL_AD_DISMISSED_MESSAGE = 'В этом путешествии реклама скрыта на 7 дней. В тарифах «Продвинутый» и «Профи» рекламы нет.';
export function getTravelAdStatus(params) {
    return apiFetch(`/travel/api/travel_plan_ad/${encodeURIComponent(params.travelPlanId)}/`, {
        baseUrl: params.baseUrl, token: params.token, method: 'GET', redirectOnUnauthorized: false,
        requestTimeoutMs: 10000,
    });
}
export function dismissTravelAd(params) {
    return apiFetch(`/travel/api/travel_plan_ad/${encodeURIComponent(params.travelPlanId)}/`, {
        baseUrl: params.baseUrl, token: params.token, method: 'POST', redirectOnUnauthorized: false,
        requestTimeoutMs: 10000,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params.dismissedUntil ? { dismissed_until: params.dismissedUntil } : {}),
    });
}
export function isBasicTravelTariff(value) {
    var _a;
    if (typeof value === 'number' || typeof value === 'string')
        return String(value).trim() === '1';
    if (value && typeof value === 'object') {
        const tariff = value;
        return isBasicTravelTariff((_a = tariff.travel_tariff_type_id) !== null && _a !== void 0 ? _a : tariff.id);
    }
    return false;
}
export function travelAdDismissalKey(viewerId, travelPlanId) {
    return `trackme.travelAd.${encodeURIComponent(viewerId)}:${encodeURIComponent(travelPlanId)}`;
}
export function travelAdDismissalExpiresAt(now) {
    return now + 7 * 24 * 60 * 60 * 1000;
}
// One session belongs to the viewer and travel, shared by every section of its card.
export function createTravelAdSession(options) {
    const now = options.now || Date.now;
    const eligible = Boolean(options.viewerId && options.travelPlanId && !options.isPublic && isBasicTravelTariff(options.tariffId));
    const key = travelAdDismissalKey(options.viewerId, options.travelPlanId);
    let snapshot = { visible: false, ready: !eligible, dismissedUntil: null };
    let dismissal = null;
    let remoteEnabled = false;
    let revision = 0;
    let refreshing = null;
    const listeners = new Set();
    const emit = (ready = true) => {
        const until = dismissal && dismissal.until > now() ? dismissal.until : null;
        snapshot = { ready, visible: eligible && ready && remoteEnabled && !until, dismissedUntil: until };
        listeners.forEach(listener => listener());
    };
    const persist = async (record) => {
        try {
            await options.storage.setItem(key, JSON.stringify(record));
        }
        catch { /* Keep dismissal in this session if storage is unavailable. */ }
    };
    const read = async () => {
        try {
            const parsed = JSON.parse(await options.storage.getItem(key) || 'null');
            return parsed && typeof parsed.until === 'number' && Number.isFinite(parsed.until) && parsed.until > now()
                ? { until: parsed.until, pending: parsed.pending === true } : null;
        }
        catch {
            return null;
        }
    };
    const applyRemote = (status) => {
        remoteEnabled = status.enabled === true;
        const until = status.dismissed_until ? Date.parse(status.dismissed_until) : NaN;
        dismissal = Number.isFinite(until) && until > now() ? { until, pending: false } : null;
        emit();
    };
    const refresh = () => {
        if (!eligible)
            return Promise.resolve();
        if (refreshing)
            return refreshing;
        const started = revision;
        refreshing = (async () => {
            const cached = await read();
            if (started !== revision)
                return;
            if (cached && (!dismissal || cached.until > dismissal.until))
                dismissal = cached;
            emit(snapshot.ready);
            try {
                let status = await options.getRemote();
                if (started !== revision)
                    return;
                if ((dismissal === null || dismissal === void 0 ? void 0 : dismissal.pending) && dismissal.until > now() && status.enabled) {
                    status = await options.dismissRemote(new Date(dismissal.until).toISOString());
                    if (started !== revision)
                        return;
                }
                applyRemote(status);
                await persist(dismissal);
            }
            catch {
                // An unknown server state never flashes an ad hidden on another device.
                emit();
            }
        })().finally(() => { refreshing = null; });
        return refreshing;
    };
    const dismiss = () => {
        if (!snapshot.visible)
            return false;
        revision += 1;
        const started = revision;
        const record = { until: travelAdDismissalExpiresAt(now()), pending: true };
        dismissal = record;
        emit();
        void (async () => {
            await persist(record);
            try {
                const status = await options.dismissRemote(new Date(record.until).toISOString());
                if (started !== revision)
                    return;
                applyRemote(status);
                // A stale or malformed response cannot undo the local close.
                if (!dismissal) {
                    dismissal = record;
                    emit();
                }
                await persist(dismissal);
            }
            catch { /* Retry the persisted dismissal on the next refresh. */ }
        })();
        return true;
    };
    return {
        key, refresh, dismiss,
        getSnapshot: () => snapshot,
        subscribe: (listener) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
        checkExpiry: () => { if (snapshot.dismissedUntil && snapshot.dismissedUntil <= now())
            emit(); },
    };
}
